<template>
  <div class="flex items-center gap-2 px-6 py-3 border-b border-border">
    <!-- Status Dropdown -->
    <select
      v-model="selectedStatus"
      @change="applyFilters"
      class="text-xs border border-border rounded-md px-2 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-ring hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <option v-for="status in statusOptions" :key="status.value" :value="status.value">
        {{ status.label }}{{ status.count !== undefined ? ` (${status.count})` : '' }}
      </option>
    </select>

    <!-- Sort -->
    <select
      v-model="selectedOrdering"
      @change="applyFilters"
      class="text-xs border border-border rounded-md px-2 py-1.5 text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-ring hover:bg-gray-50 transition-colors cursor-pointer"
    >
      <option value="-payment_date">Newest first</option>
      <option value="payment_date">Oldest first</option>
    </select>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import type { PaymentOrdering, PaymentStatus } from '@/types/subscription'

interface StatusOption {
  value: PaymentStatus | 'all'
  label: string
  count?: number
}

interface Props {
  statusCounts?: Record<PaymentStatus, number>
  totalCount?: number
  selectedStatus?: PaymentStatus | 'all'
}

interface Emits {
  (e: 'filters-change', filters: {
    ordering?: PaymentOrdering
    status?: PaymentStatus | 'all'
  }): void
}

const props = withDefaults(defineProps<Props>(), {
  statusCounts: () => ({
    failed: 0,
    canceled: 0,
    pending: 0,
    paid: 0
  }),
  totalCount: 0,
  selectedStatus: 'all'
})

const emit = defineEmits<Emits>()

// Reactive state
const selectedOrdering = ref<PaymentOrdering>('-payment_date')
const selectedStatus = ref<PaymentStatus | 'all'>(props.selectedStatus)

// Constants
const AVAILABLE_STATUSES: ReadonlyArray<{ value: PaymentStatus; label: string }> = [
  { value: 'paid', label: 'Paid' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'canceled', label: 'Canceled' }
] as const

// Status options
const statusOptions = computed<StatusOption[]>(() => {
  const options: StatusOption[] = [
    {
      value: 'all',
      label: 'All Payments',
      count: props.totalCount
    }
  ]
  
  // Only show status options that have at least 1 payment
  const statusesWithPayments = AVAILABLE_STATUSES.filter(({ value }) => {
    const count = props.statusCounts[value] || 0
    return count > 0
  }).map(({ value, label }) => ({
    value,
    label,
    count: props.statusCounts[value]
  }))
  
  return [...options, ...statusesWithPayments]
})

// Methods
const applyFilters = (): void => {
  const filters: {
    ordering: PaymentOrdering
    status?: PaymentStatus | 'all'
  } = {
    ordering: selectedOrdering.value,
    status: selectedStatus.value
  }

  emit('filters-change', filters)
}

// Watch ordering changes
watch(selectedOrdering, applyFilters)
</script>
